import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EApiLanguageCode, TVocabularyBookCategory, TVocabularyBookLevel } from '@datatype/iyagigo/api-type';
import {
  TAdminClozeType,
  TAdminSortOrder,
  TAdminSourceSort,
} from '@datatype/admin/api-type';

export type TAdminMainMenu = 'student' | 'expression' | 'cloze' | 'vocabulary-book';

export interface IExpressionFilter {
  languageCode: EApiLanguageCode | null;
  sortBy: TAdminSourceSort | null;
  sortOrder: TAdminSortOrder;
}

export interface IClozeFilter {
  clozeType: TAdminClozeType | null;
  languageCode: EApiLanguageCode | null;
}

export interface IVocabularyBookFilter {
  languageCode: EApiLanguageCode | null;
  category: TVocabularyBookCategory | null;
  levels: TVocabularyBookLevel[] | null;
  published: boolean | null;
}

interface IState {
  mainMenu: TAdminMainMenu;
  selectedSourceKey: string | null;
  selectedClozeKey: string | null;
  selectedVocabularyBookKey: string | null;
  selectedMetricDate: string | null;
  expressionFilter: IExpressionFilter;
  clozeFilter: IClozeFilter;
  vocabularyBookFilter: IVocabularyBookFilter;
}

const initialExpressionFilter: IExpressionFilter = {
  languageCode: null,
  sortBy: null,
  sortOrder: 'desc',
};

const initialClozeFilter: IClozeFilter = {
  clozeType: null,
  languageCode: null,
};

const initialVocabularyBookFilter: IVocabularyBookFilter = {
  languageCode: null,
  category: null,
  levels: null,
  published: null,
};

const initialState: IState = {
  mainMenu: 'student',
  selectedSourceKey: null,
  selectedClozeKey: null,
  selectedVocabularyBookKey: null,
  selectedMetricDate: null,
  expressionFilter: initialExpressionFilter,
  clozeFilter: initialClozeFilter,
  vocabularyBookFilter: initialVocabularyBookFilter,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    initialize: (state) => {
      for (const key in state) {
        state[key] = initialState[key];
      }
    },
    storeMainMenu: (state, action: PayloadAction<TAdminMainMenu>) => {
      state.mainMenu = action.payload;
    },
    storeSelectedSourceKey: (state, action: PayloadAction<string | null>) => {
      state.selectedSourceKey = action.payload;
    },
    storeSelectedClozeKey: (state, action: PayloadAction<string | null>) => {
      state.selectedClozeKey = action.payload;
    },
    storeSelectedMetricDate: (state, action: PayloadAction<string | null>) => {
      state.selectedMetricDate = action.payload;
    },
    storeExpressionFilter: (state, action: PayloadAction<Partial<IExpressionFilter>>) => {
      state.expressionFilter = { ...state.expressionFilter, ...action.payload };
    },
    resetExpressionFilter: (state) => {
      state.expressionFilter = initialExpressionFilter;
    },
    storeClozeFilter: (state, action: PayloadAction<Partial<IClozeFilter>>) => {
      state.clozeFilter = { ...state.clozeFilter, ...action.payload };
    },
    resetClozeFilter: (state) => {
      state.clozeFilter = initialClozeFilter;
    },
    storeSelectedVocabularyBookKey: (state, action: PayloadAction<string | null>) => {
      state.selectedVocabularyBookKey = action.payload;
    },
    storeVocabularyBookFilter: (state, action: PayloadAction<Partial<IVocabularyBookFilter>>) => {
      state.vocabularyBookFilter = { ...state.vocabularyBookFilter, ...action.payload };
    },
    resetVocabularyBookFilter: (state) => {
      state.vocabularyBookFilter = initialVocabularyBookFilter;
    },
  },
});

export const actions = adminSlice.actions;
export default adminSlice.reducer;
